import { ActionTypes, AmazonLoginResponseType, AmazonLoginAuthStatus, AmazonLoginStatus } from '../constants/AppConstants';
import AppDispatcher from '../dispatcher/AppDispatcher';
import AmazonLoginService from '../services/AmazonLoginService';
import AmazonPayService from '../services/AmazonPayService';
import { AmazonLoginError } from '../errors/AmazonLoginError';
import CustomerActions from './CustomerActions';

const AmazonLoginActions = {
    async authenticate({ accessToken }) {

        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_PROCESSING_STARTED
        });

        try {
            const { status, token } = await AmazonLoginService.authenticate({ accessToken });

            if (status === AmazonLoginAuthStatus.MISMATCHED_EMAILS) {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                    status: AmazonLoginStatus.SYNC_EMAIL_START,
                    customer: token.customer.toJS()
                });
            }

            if (token && token.isValid()) {
                await CustomerActions.loginWithToken({ token });

                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                    status: AmazonLoginStatus.LOGIN_SUCCESS
                });
            } else {
                throw new AmazonLoginError('No access token available in the auth response');
            }
        } catch (error) {
            let rethrow = true;

            if (error instanceof AmazonLoginError) {
                switch (error.message) {
                    case AmazonLoginAuthStatus.LINKING_REQUIRED:
                        rethrow = false;

                        AppDispatcher.dispatch({
                            actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                            status: AmazonLoginStatus.LINK_START,
                            customer: error.extra.customer
                        });
                        break;

                    case AmazonLoginAuthStatus.LINKING_NOT_AVAILABLE:
                        rethrow = false;

                        AppDispatcher.dispatch({
                            actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                            status: AmazonLoginStatus.REGISTER_START,
                            customer: error.extra.customer
                        });
                        break;
                }
            }

            if (rethrow) {
                console.error(error);

                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                    status: AmazonLoginStatus.LOGIN_ERROR
                });
            }
        } finally {
            AppDispatcher.dispatch({
                actionType: ActionTypes.AMAZON_LOGIN_PROCESSING_FINISHED
            });
        }
    },

    resetStateToIdle() {
        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_STATUS,
            status: AmazonLoginStatus.IDLE
        });
    },

    handleOnError(error) {
        console.error(error);

        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_STATUS,
            status: AmazonLoginStatus.LOGIN_ERROR
        });
    },

    handleOnLoginComplete(hasLoginCompleted, accessToken) {
        const statusProps = accessToken ? { status: AmazonLoginStatus.LOGIN_READY_TO_AUTH } : null;

        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_LOGIN_ACCESS_TOKEN,
            accessToken,
            hasLoginCompleted,
            ...statusProps
        });
    },

    handleOnRetrieveProfile(response) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_EMAIL,
            amazonEmail: response.profile.PrimaryEmail
        });
    },

    async linkAccount({ email, password }) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_PROCESSING_STARTED
        });

        try {
            await AmazonLoginService.linkAccount({ email, password });

            AppDispatcher.dispatch({
                actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                status: AmazonLoginStatus.LINK_SUCCESS
            });

            // Automatically log-in user as they authenticated themselves
            // against their OTRL account during the linking process
            CustomerActions.login({ username: email, password });
        } catch (error) {
            if (error.name === 'UnauthorisedError') {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                    status: AmazonLoginStatus.LINK_ERROR
                });
            } else {
                throw error;
            }
        } finally {
            AppDispatcher.dispatch({
                actionType: ActionTypes.AMAZON_LOGIN_PROCESSING_FINISHED
            });
        }
    },

    async load(_options = {}) {
        const defaults = {
            isAmazonLogin: true,
            responseType: AmazonLoginResponseType.TOKEN,
        };
        const options = { ...defaults, ..._options };

        await AmazonPayService.loadSdk({
            options,
            handleOnError: this.handleOnError,
            handleOnLoginComplete: this.handleOnLoginComplete,
        });

        AmazonLoginService.init({
            options: {
                handleOnRetrieveProfile: this.handleOnRetrieveProfile
            }
        });
    },

    async login() {
        AmazonPayService.authorizeLogin(accessToken => {
            window.amazon.Login.retrieveProfile(accessToken, (/* response */) => {
                // If you'd ever need to get customer's data (full name, email, postcode and Amazon ID)
                // from Amazon Login JavaScript SDK then response is the object you're looking for.
            });
        });
    },

    logout() {
        if (window.amazon && window.amazon.Login) {
            window.amazon.Login.logout();
            // In Booking Engine there are two Amazon Login/Pay sign in buttons.
            // Because Amazon SDK keeps track of an exact URL that were used
            // for a sign-in in a given session it's impossible to sign in again
            // using a sign in button on a different URL as it will raise the
            // "proxy parent uri != topic target uri" error.
            //
            // To replicate an issue that below fixes. Try:
            // - signing in on the /delivery pages using Amazon Pay shortcut button
            // - click the logout Amazon button above the Amazon widgets
            // - try to sign in again on the /payment page
            //
            // As a workaround reload the page to unload the Amazon SDK JavaScript.
            setTimeout(() => window.location.reload(), 250);
        } else {
            console.warn('Cannot log out from Amazon.');
        }
    },

    resetStatus(status = AmazonLoginStatus.IDLE) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_STATUS,
            status
        });
    },

    retrieveProfile(accessToken) {
        AmazonLoginService.retrieveProfile(accessToken);
    },

    async unlinkAccount() {
        AppDispatcher.dispatch({
            actionType: ActionTypes.AMAZON_LOGIN_PROCESSING_STARTED
        });

        try {
            await AmazonLoginService.unlinkAccount();

            AppDispatcher.dispatch({
                actionType: ActionTypes.AMAZON_LOGIN_STATUS,
                status: AmazonLoginStatus.UNLINK_SUCCESS
            });
        } catch (error) {
            throw error;
        } finally {
            AppDispatcher.dispatch({
                actionType: ActionTypes.AMAZON_LOGIN_PROCESSING_FINISHED
            });
        }
    }
};

export default AmazonLoginActions;



// WEBPACK FOOTER //
// ./src/actions/AmazonLoginActions.js